import type { Milestone } from '@just-us/shared'
/**
 * components/milestone-detail-sheet.tsx
 *
 * Bottom sheet showing full milestone details.
 * - Full title, date, note
 * - Horizontal photo strip — tappable to open lightbox
 * - Edit and Delete buttons
 */
import { useState } from 'react'

interface MilestoneDetailSheetProps {
  milestone: Milestone
  onEdit: (milestone: Milestone) => void
  onDelete: (milestone: Milestone) => void
  onClose: () => void
}

const formatFullDate = (dateStr: string): string => {
  const d = new Date(`${dateStr}T00:00:00`)
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
}

// ---------------------------------------------------------------------------
// Lightbox — full-screen image overlay
// ---------------------------------------------------------------------------

interface LightboxProps {
  urls: string[]
  initialIndex: number
  onClose: () => void
}

function Lightbox({ urls, initialIndex, onClose }: LightboxProps) {
  const [index] = useState(initialIndex)
  const url = urls[index]

  return (
    <dialog
      className="fixed inset-0 z-[100] bg-black/90 flex flex-col items-center justify-center animate-fade-in m-0 w-full h-full max-w-none"
      open
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose()
      }}
      aria-modal="true"
    >
      {/* Close button */}
      <button
        type="button"
        onClick={onClose}
        className="
          absolute top-4 right-4 z-10
          w-[44px] h-[44px] rounded-full
          bg-white/20 backdrop-blur-sm
          flex items-center justify-center
          text-white text-[18px]
          active:scale-95 transition-all duration-150
          touch-manipulation
        "
        aria-label="Close"
      >
        ×
      </button>

      {/* Image */}
      <img
        key={url}
        src={url}
        alt={`Milestone moment ${index + 1}`}
        className="max-w-full max-h-full object-contain animate-fade-in"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose()
        }}
      />
    </dialog>
  )
}

// ---------------------------------------------------------------------------
// MilestoneDetailSheet
// ---------------------------------------------------------------------------

export default function MilestoneDetailSheet({
  milestone,
  onEdit,
  onDelete,
  onClose,
}: MilestoneDetailSheetProps) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)

  const photoUrls = milestone.photo_urls ?? []
  const hasPhotos = photoUrls.length > 0

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-[rgba(44,24,16,0.25)] z-[60] backdrop-blur-[2px] animate-fade-in"
        onClick={onClose}
        onKeyDown={(e) => e.key === 'Escape' && onClose()}
        role="button"
        tabIndex={-1}
        aria-label="Close"
      />

      {/* Sheet */}
      <div
        className="
        fixed bottom-0 left-0 right-0 z-[70]
        bg-white rounded-t-[24px]
        shadow-[0_-4px_40px_rgba(0,0,0,0.12)]
        animate-slide-up
        pb-[env(safe-area-inset-bottom)]
        max-w-[430px] mx-auto
        max-h-[85vh] overflow-y-auto
      "
      >
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1 sticky top-0 bg-white z-1">
          <div className="w-[36px] h-[4px] rounded-full bg-[#F4C0D1]" />
        </div>

        {/* Header with title */}
        <div className="px-5 pb-4 border-b border-[#F9E0E8]">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <h2 className="font-['Playfair_Display'] text-[22px] font-medium text-[#2C1810] leading-tight">
                {milestone.title}
              </h2>
              <p className="font-['DM_Sans'] text-[13px] text-[#B89AA2] mt-1">
                {formatFullDate(milestone.date)}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="
                shrink-0 w-[32px] h-[32px] rounded-full
                bg-[#FBEAF0] border border-[#F4C0D1]
                flex items-center justify-center
                text-[#7A5A62] text-[16px]
                active:scale-90 active:bg-[#F4C0D1]
                transition-all duration-150 touch-manipulation
              "
              aria-label="Close"
            >
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-5 py-4 flex flex-col gap-5">
          {/* Note */}
          {milestone.note && (
            <div>
              <p className="font-['DM_Sans'] text-[15px] text-[#7A5A62] leading-relaxed whitespace-pre-wrap">
                {milestone.note}
              </p>
            </div>
          )}

          {/* Photo strip */}
          {hasPhotos && (
            <div className="flex flex-col gap-2">
              <div className="flex gap-2 overflow-x-auto scrollbar-none pb-1">
                {photoUrls.map((url, i) => (
                  <button
                    key={url}
                    type="button"
                    onClick={() => setLightboxIndex(i)}
                    onKeyDown={(e) => e.key === 'Enter' && setLightboxIndex(i)}
                    className="
                      shrink-0 rounded-[12px] overflow-hidden
                      border border-[#F4C0D1]
                      active:scale-95 transition-all duration-150
                      touch-manipulation
                    "
                    style={{ minWidth: '100px', minHeight: '100px' }}
                    aria-label={`View moment ${i + 1}`}
                  >
                    <img
                      src={url}
                      alt={`Moment ${i + 1}`}
                      className="w-[100px] h-[100px] object-cover"
                    />
                  </button>
                ))}
              </div>
              <p className="font-['DM_Sans'] text-[11px] text-[#B89AA2]">Tap to view</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2 border-t border-[#F9E0E8]">
            <button
              type="button"
              onClick={() => onEdit(milestone)}
              className="
                flex-1 h-[48px] px-4
                bg-[#FBEAF0] text-[#D4537E]
                font-['DM_Sans'] text-[14px] font-medium
                rounded-[12px] border border-[#F4C0D1]
                active:scale-[0.98] active:bg-[#F4C0D1]
                transition-all duration-150 touch-manipulation
              "
            >
              ✏️ Edit
            </button>
            <button
              type="button"
              onClick={() => onDelete(milestone)}
              className="
                w-[48px] h-[48px] px-4
                rounded-[12px] border border-[#F5C6CB]
                active:bg-[rgba(192,57,43,0.06)]
                active:scale-[0.98]
                transition-all duration-150 touch-manipulation
                flex items-center justify-center
                text-[#C0392B]
              "
              aria-label="Delete milestone"
            >
              🗑️
            </button>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <Lightbox
          urls={photoUrls}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </>
  )
}
